/**
 * Operations Calendar Page
 *
 * Main page for the operations calendar view.
 */

import { useState } from 'react';
import { Calendar, Filter, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { OperationsCalendar } from '../components/OperationsCalendar';
import { CalendarFilters } from '../components/CalendarFilters';
import { CalendarLegend } from '../components/CalendarLegend';
import { calendarKeys } from '../hooks';
import type { CalendarFilters as CalendarFiltersType } from '../types';
import { DEFAULT_CALENDAR_FILTERS } from '../types';

export function CalendarPage() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<CalendarFiltersType>(DEFAULT_CALENDAR_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: calendarKeys.all });
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex-none p-4 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Calendar className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-semibold">Operations Calendar</h1>
              <p className="text-sm text-muted-foreground">
                View and manage scheduled projects, tasks, and milestones
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Filters (Collapsible) */}
      <Collapsible open={filtersOpen} onOpenChange={setFiltersOpen}>
        <div className="flex-none px-4 py-2 border-b bg-muted/30">
          <div className="flex items-center justify-between">
            <CalendarLegend />
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm">
                <Filter className="h-4 w-4 mr-2" />
                Filters
                {filtersOpen ? (
                  <ChevronUp className="h-4 w-4 ml-1" />
                ) : (
                  <ChevronDown className="h-4 w-4 ml-1" />
                )}
              </Button>
            </CollapsibleTrigger>
          </div>
        </div>
        <CollapsibleContent>
          <div className="px-4 py-3 border-b bg-muted/10">
            <CalendarFilters
              filters={filters}
              onFiltersChange={setFilters}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Calendar Content */}
      <div className="flex-1 overflow-auto">
        <Card className="m-4">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">Schedule</CardTitle>
                <CardDescription>
                  Drag events to reschedule. Click for details.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <OperationsCalendar filters={filters} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
